import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import Logo from "@/components/Logo";

function formatErr(d) {
  if (d == null) return "Login failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}

export default function AdminLogin() {
  const { user, login, ready } = useAuth();
  const [email, setEmail] = useState("admin@gawx.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (ready && user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center px-4">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/9751579/pexels-photo-9751579.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-black/70" />

      <form
        onSubmit={submit}
        className="glass relative z-10 w-full max-w-md rounded-sm border-white/15 p-8 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
      >
        <div className="mb-6 flex items-center gap-3">
          <Logo size={56} />
          <div>
            <div className="font-oswald text-xs uppercase tracking-[0.3em] text-gray-400">GAWX Control Room</div>
            <div className="font-oswald text-xl font-bold uppercase tracking-widest text-white">Admin Sign In</div>
          </div>
        </div>

        <label className="mb-2 block font-oswald text-xs uppercase tracking-widest text-gray-300">Email</label>
        <input
          data-testid={ADMIN.loginEmail}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-sm border border-white/15 bg-black/50 px-3 py-2 text-white outline-none focus:border-[#1f63c9]"
          required
        />

        <label className="mb-2 mt-4 block font-oswald text-xs uppercase tracking-widest text-gray-300">Password</label>
        <input
          data-testid={ADMIN.loginPassword}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-sm border border-white/15 bg-black/50 px-3 py-2 text-white outline-none focus:border-[#1f63c9]"
          required
        />

        <button
          type="submit"
          data-testid={ADMIN.loginSubmit}
          disabled={busy}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-sm bg-[#1f63c9] px-4 py-3 font-oswald text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#2a74d8] disabled:opacity-60"
        >
          <LogIn className="h-4 w-4" />
          {busy ? "Signing in…" : "Enter Control Room"}
        </button>

        <div className="mt-4 text-center text-xs text-gray-500">
          Authorized personnel only
        </div>
      </form>
    </div>
  );
}
